import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  HospitalityAvailability,
  HospitalityProperty,
  HospitalityRatePlan,
  HospitalityReservation,
  HospitalityRoomType,
  HospitalityStay
} from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-hospitality-reservation-detail',
  imports: [
    DecimalPipe, DatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    ButtonModule, InputTextModule, InputNumberModule, SelectModule, CheckboxModule, JalaliDateInputComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('catalog.hospitality.reservations.createTitle') : t('catalog.hospitality.reservations.detailTitle')"
      [description]="t('catalog.hospitality.reservations.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !reservation()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error()" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.hospitality.reservations.newRecord') : reservation()!.reservationReference }}</h3>
            <p>{{ isCreateMode() ? t('catalog.hospitality.reservations.createHint') : reservation()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/hospitality/reservations']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            }
          </div>
        </section>

        @if (isCreateMode()) {
          <section class="form-grid">
            <label><span>{{ t('catalog.hospitality.reservationReference') }}</span><input pInputText [(ngModel)]="form.reservationReference" /></label>
            <label><span>{{ t('catalog.service.customerReference') }}</span><input pInputText [(ngModel)]="form.customerReference" /></label>
            <label><span>{{ t('catalog.hospitality.property') }}</span>
              <p-select [options]="propertyOptions()" [(ngModel)]="form.propertyId" optionLabel="label" optionValue="value" [filter]="true" appendTo="body" (ngModelChange)="onPropertyChange()" />
            </label>
            <label><span>{{ t('catalog.hospitality.roomType') }}</span>
              <p-select [options]="roomTypeOptions()" [(ngModel)]="form.roomTypeId" optionLabel="label" optionValue="value" [filter]="true" appendTo="body" (ngModelChange)="previewAvailability()" />
            </label>
            <label><span>{{ t('catalog.hospitality.ratePlan') }}</span>
              <p-select [options]="ratePlanOptions()" [(ngModel)]="form.ratePlanId" optionLabel="label" optionValue="value" [filter]="true" appendTo="body" (ngModelChange)="previewAvailability()" />
            </label>
            <label><span>{{ t('catalog.hospitality.checkIn') }}</span><ain-date-input [(ngModel)]="form.checkInDate" (ngModelChange)="previewAvailability()" /></label>
            <label><span>{{ t('catalog.hospitality.checkOut') }}</span><ain-date-input [(ngModel)]="form.checkOutDate" (ngModelChange)="previewAvailability()" /></label>
            <label><span>{{ t('catalog.hospitality.guestCount') }}</span><p-inputNumber [(ngModel)]="form.guestCount" [min]="1" /></label>
          </section>
          @if (availability()) {
            <section class="availability-preview">
              <strong>{{ t('catalog.hospitality.availabilityPreview') }}</strong>
              <span>{{ t('catalog.hospitality.estimatedTotal') }}: {{ availability()!.estimatedStayTotal | number:'1.2-2' }}</span>
              <span>{{ t('catalog.hospitality.nightlyRate') }}: {{ availability()!.suggestedNightlyRate | number:'1.2-2' }}</span>
              <span>{{ t('catalog.hospitality.kpi.occupiedRooms') }}: {{ availability()!.availableRooms }} / {{ availability()!.totalRooms }}</span>
            </section>
          }
        } @else if (reservation()) {
          <section class="summary-grid">
            <article><strong>{{ t('catalog.service.customerReference') }}</strong><span>{{ reservation()!.customerReference }}</span></article>
            <article><strong>{{ t('catalog.hospitality.checkIn') }}</strong><span>{{ reservation()!.checkInDateUtc | date:'short' }}</span></article>
            <article><strong>{{ t('catalog.hospitality.checkOut') }}</strong><span>{{ reservation()!.checkOutDateUtc | date:'short' }}</span></article>
            <article><strong>{{ t('catalog.hospitality.estimatedTotal') }}</strong><span>{{ reservation()!.estimatedTotal | number:'1.2-2' }}</span></article>
          </section>

          @if (reservation()!.status === 'Confirmed') {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.hospitality.lifecycle.checkIn') }}</h4>
              <p-select
                [options]="assignableRoomOptions()"
                [(ngModel)]="checkInRoomId"
                optionLabel="label"
                optionValue="value"
                [placeholder]="t('catalog.hospitality.assignRoom')"
                [filter]="true"
                appendTo="body" />
              <button pButton type="button" [label]="t('catalog.hospitality.action.checkIn')" [disabled]="acting()" (click)="checkIn()"></button>
            </section>

            <section class="lifecycle-panel">
              <h4>{{ t('catalog.hospitality.lifecycle.cancel') }}</h4>
              <label class="checkbox-row"><p-checkbox [(ngModel)]="cancelForm.policyExceptionApproved" [binary]="true" /> {{ t('catalog.hospitality.policyExceptionApproved') }}</label>
              <div class="charge-form">
                <input pInputText [(ngModel)]="cancelForm.reason" [placeholder]="t('catalog.hospitality.cancelReason')" />
                <button pButton type="button" severity="danger" [label]="t('catalog.hospitality.action.cancelReservation')" [disabled]="acting()" (click)="cancelReservation()"></button>
                <button pButton type="button" severity="secondary" [label]="t('catalog.hospitality.action.markNoShow')" [disabled]="acting()" (click)="markNoShow()"></button>
              </div>
            </section>

            <section class="lifecycle-panel">
              <h4>{{ t('catalog.hospitality.lifecycle.financial') }}</h4>
              @if (reservation()!.financialSummary) {
                <div class="financial-summary">
                  <span>{{ t('catalog.hospitality.prepaymentReceived') }}: {{ reservation()!.financialSummary!.prepaymentReceived | number:'1.2-2' }}</span>
                  <span>{{ t('catalog.hospitality.prepaymentAvailable') }}: {{ reservation()!.financialSummary!.prepaymentAvailable | number:'1.2-2' }}</span>
                  @if (reservation()!.financialSummary!.channelCommissionAmount > 0) {
                    <span>{{ t('catalog.hospitality.channelCommission') }}: {{ reservation()!.financialSummary!.channelCode }} — {{ reservation()!.financialSummary!.channelCommissionAmount | number:'1.2-2' }}</span>
                  }
                </div>
              }
              <div class="charge-form">
                <p-inputNumber [(ngModel)]="prepaymentForm.amount" [min]="0" [placeholder]="t('catalog.hospitality.prepaymentAmount')" />
                <button pButton type="button" [label]="t('catalog.hospitality.action.receivePrepayment')" [disabled]="acting()" (click)="receivePrepayment()"></button>
              </div>
              @if (!reservation()!.financialSummary?.channelCommissionAmount) {
                <div class="charge-form">
                  <input pInputText [(ngModel)]="commissionForm.channelCode" [placeholder]="t('catalog.hospitality.channelCode')" />
                  <p-inputNumber [(ngModel)]="commissionForm.amount" [min]="0" />
                  <button pButton type="button" [label]="t('catalog.hospitality.action.postCommission')" [disabled]="acting()" (click)="postChannelCommission()"></button>
                </div>
              }
              @if ((reservation()!.financialSummary?.prepaymentAvailable ?? 0) > 0) {
                <div class="charge-form">
                  <input pInputText [(ngModel)]="refundForm.reason" [placeholder]="t('catalog.hospitality.refundReason')" />
                  <p-inputNumber [(ngModel)]="refundForm.amount" [min]="0" />
                  <button pButton type="button" severity="secondary" [label]="t('catalog.hospitality.action.postRefund')" [disabled]="acting()" (click)="postRefund()"></button>
                </div>
              }
            </section>
          }

          @if (stay()) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.hospitality.lifecycle.folio') }}</h4>
              <p>{{ t('catalog.hospitality.folioBalance') }}: {{ stay()!.folio?.balanceDue | number:'1.2-2' }}</p>
              @if (stay()!.folio?.lines?.length) {
                <ul>
                  @for (line of stay()!.folio!.lines; track line.id) {
                    <li>{{ line.chargeType }} — {{ line.description }} — {{ line.amount | number:'1.2-2' }}</li>
                  }
                </ul>
              }
              @if (stay()!.status === 'Active') {
                <div class="charge-form">
                  <p-select [options]="chargeTypeOptions" [(ngModel)]="chargeForm.chargeType" optionLabel="label" optionValue="value" appendTo="body" />
                  <input pInputText [(ngModel)]="chargeForm.description" [placeholder]="t('catalog.hospitality.chargeDescription')" />
                  <p-inputNumber [(ngModel)]="chargeForm.amount" [min]="0" />
                  <button pButton type="button" [label]="t('catalog.hospitality.action.postCharge')" [disabled]="acting()" (click)="postCharge()"></button>
                  <button pButton type="button" severity="danger" [label]="t('catalog.hospitality.action.checkOut')" [disabled]="acting()" (click)="checkOut()"></button>
                </div>
                <div class="charge-form minibar-form">
                  <strong>{{ t('catalog.hospitality.minibar.title') }}</strong>
                  <input pInputText [(ngModel)]="minibarForm.itemCode" [placeholder]="t('catalog.hospitality.minibar.itemCode')" />
                  <input pInputText [(ngModel)]="minibarForm.itemName" [placeholder]="t('catalog.hospitality.minibar.itemName')" />
                  <p-inputNumber [(ngModel)]="minibarForm.quantity" [min]="1" />
                  <p-inputNumber [(ngModel)]="minibarForm.unitPrice" [min]="0" />
                  <button pButton type="button" [label]="t('catalog.hospitality.minibar.post')" [disabled]="acting()" (click)="postMinibar()"></button>
                </div>
              }
            </section>
          }
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 16px; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; }
    .actions { display: flex; gap: 12px; align-items: center; }
    .form-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
    .form-grid label { display: grid; gap: 6px; }
    .availability-preview { display: flex; flex-wrap: wrap; gap: 16px; padding: 12px; margin-bottom: 16px; background: #f0fdf4; border-radius: 8px; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
    .summary-grid article { display: grid; gap: 4px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; }
    .lifecycle-panel { margin-top: 16px; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; display: grid; gap: 10px; }
    .financial-summary { display: flex; flex-wrap: wrap; gap: 16px; font-size: 0.9rem; color: #374151; }
    .charge-form { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .minibar-form { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #e5e7eb; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    @media (max-width: 1200px) { .form-grid, .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media (max-width: 760px) { .form-grid, .summary-grid { grid-template-columns: 1fr; } }
  `]
})
export class HospitalityReservationDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly acting = signal(false);
  readonly reservation = signal<HospitalityReservation | null>(null);
  readonly stay = signal<HospitalityStay | null>(null);
  readonly properties = signal<HospitalityProperty[]>([]);
  readonly roomTypes = signal<HospitalityRoomType[]>([]);
  readonly ratePlans = signal<HospitalityRatePlan[]>([]);
  readonly availability = signal<HospitalityAvailability | null>(null);
  readonly assignableRooms = signal<{ label: string; value: string }[]>([]);
  readonly error = signal('');

  readonly chargeTypeOptions = [
    { label: 'Room', value: 'room' },
    { label: 'Minibar', value: 'minibar' },
    { label: 'Restaurant', value: 'restaurant' },
    { label: 'Misc', value: 'misc' }
  ];

  checkInRoomId = '';

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly propertyOptions = computed(() =>
    this.properties().map(p => ({ label: `${p.propertyCode} — ${p.name}`, value: p.id }))
  );
  readonly roomTypeOptions = computed(() =>
    this.roomTypes().map(rt => ({ label: `${rt.typeCode} — ${rt.name}`, value: rt.id }))
  );
  readonly ratePlanOptions = computed(() =>
    this.ratePlans().map(rp => ({ label: `${rp.planCode} — ${rp.name}`, value: rp.id }))
  );
  readonly assignableRoomOptions = computed(() => this.assignableRooms());

  form = {
    reservationReference: '',
    customerReference: '',
    propertyId: '',
    roomTypeId: '',
    ratePlanId: '',
    checkInDate: '',
    checkOutDate: '',
    guestCount: 2
  };

  chargeForm = { chargeType: 'misc', description: '', amount: 0 };
  prepaymentForm = { amount: 0 };
  refundForm = { amount: 0, reason: '' };
  commissionForm = { channelCode: '', amount: 0 };
  cancelForm = { reason: '', policyExceptionApproved: false };
  minibarForm = { itemCode: '', itemName: '', quantity: 1, unitPrice: 0 };

  ngOnInit(): void {
    this.service.listHospitalityProperties().subscribe({
      next: rows => this.properties.set(rows),
      error: () => this.properties.set([])
    });

    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadReservation(id);
  }

  onPropertyChange(): void {
    const propertyId = this.form.propertyId;
    this.form.roomTypeId = '';
    this.form.ratePlanId = '';
    this.ratePlans.set([]);
    this.availability.set(null);
    if (!propertyId) {
      this.roomTypes.set([]);
      return;
    }
    this.service.listHospitalityRoomTypes(propertyId).subscribe({
      next: rows => this.roomTypes.set(rows),
      error: () => this.roomTypes.set([])
    });
    this.service.listHospitalityRatePlans(propertyId).subscribe({
      next: rows => this.ratePlans.set(rows),
      error: () => this.ratePlans.set([])
    });
  }

  previewAvailability(): void {
    if (!this.form.propertyId || !this.form.roomTypeId || !this.form.checkInDate || !this.form.checkOutDate) {
      this.availability.set(null);
      return;
    }
    const checkIn = new Date(this.form.checkInDate);
    const checkOut = new Date(this.form.checkOutDate);
    checkOut.setDate(checkOut.getDate() + 1);
    this.service.getHospitalityAvailability({
      propertyId: this.form.propertyId,
      roomTypeId: this.form.roomTypeId,
      checkInDateUtc: checkIn.toISOString(),
      checkOutDateUtc: checkOut.toISOString(),
      ratePlanId: this.form.ratePlanId || undefined
    }).subscribe({
      next: row => this.availability.set(row),
      error: () => this.availability.set(null)
    });
  }

  loadReservation(id: string): void {
    this.loading.set(true);
    this.service.getHospitalityReservation(id).subscribe({
      next: reservation => {
        this.reservation.set(reservation);
        if (reservation.status === 'Confirmed') {
          this.loadAssignableRooms(reservation.propertyId, reservation.roomTypeId);
        }
        if (reservation.status === 'CheckedIn' || reservation.status === 'CheckedOut') {
          this.loadStayForReservation(reservation.id);
        }
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.hospitality.reservationNotFound'));
        this.loading.set(false);
      }
    });
  }

  loadAssignableRooms(propertyId: string, roomTypeId: string): void {
    this.service.listAssignableRooms(propertyId, roomTypeId).subscribe({
      next: rows => this.assignableRooms.set(rows.map(r => ({
        label: `${r.roomNumber}${r.floor ? ` (${r.floor})` : ''} — ${r.status}`,
        value: r.roomUnitId
      }))),
      error: () => this.assignableRooms.set([])
    });
  }

  loadStayForReservation(reservationId: string): void {
    this.service.getHospitalityStayByReservation(reservationId).subscribe({
      next: stay => this.stay.set(stay),
      error: () => this.stay.set(null)
    });
  }

  save(): void {
    if (!this.form.propertyId || !this.form.roomTypeId || !this.form.checkInDate || !this.form.checkOutDate) return;

    this.saving.set(true);
    const checkIn = new Date(this.form.checkInDate);
    const checkOut = new Date(this.form.checkOutDate);
    checkOut.setDate(checkOut.getDate() + 1);

    this.service.createHospitalityReservation({
      propertyId: this.form.propertyId,
      roomTypeId: this.form.roomTypeId,
      ratePlanId: this.form.ratePlanId || null,
      reservationReference: this.form.reservationReference,
      customerReference: this.form.customerReference,
      checkInDateUtc: checkIn.toISOString(),
      checkOutDateUtc: checkOut.toISOString(),
      guestCount: this.form.guestCount,
      idempotencyKey: crypto.randomUUID()
    }).subscribe({
      next: created => {
        this.saving.set(false);
        void this.router.navigate(['/hospitality/reservations', created.id]);
      },
      error: () => this.saving.set(false)
    });
  }

  checkIn(): void {
    const id = this.reservation()?.id;
    if (!id) return;

    this.acting.set(true);
    this.service.checkInHospitalityReservation(id, {
      roomUnitId: this.checkInRoomId || null
    }).subscribe({
      next: stay => {
        this.stay.set(stay);
        this.reservation.update(r => r ? { ...r, status: 'CheckedIn' } : r);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  cancelReservation(): void {
    const id = this.reservation()?.id;
    if (!id) return;
    this.acting.set(true);
    this.service.cancelHospitalityReservation(id, {
      reason: this.cancelForm.reason || null,
      policyExceptionApproved: this.cancelForm.policyExceptionApproved
    }).subscribe({
      next: () => this.loadReservation(id),
      error: () => this.acting.set(false),
      complete: () => this.acting.set(false)
    });
  }

  markNoShow(): void {
    const id = this.reservation()?.id;
    if (!id) return;
    this.acting.set(true);
    this.service.markHospitalityNoShow(id, {
      reason: this.cancelForm.reason || null,
      policyExceptionApproved: this.cancelForm.policyExceptionApproved
    }).subscribe({
      next: () => this.loadReservation(id),
      error: () => this.acting.set(false),
      complete: () => this.acting.set(false)
    });
  }

  postCharge(): void {
    const stayId = this.stay()?.id;
    if (!stayId || this.chargeForm.amount <= 0) return;

    this.acting.set(true);
    this.service.postHospitalityFolioCharge(stayId, {
      chargeType: this.chargeForm.chargeType,
      description: this.chargeForm.description || this.t('catalog.hospitality.miscCharge'),
      amount: this.chargeForm.amount
    }).subscribe({
      next: stay => {
        this.stay.set(stay);
        this.chargeForm = { chargeType: 'misc', description: '', amount: 0 };
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  postMinibar(): void {
    const stayId = this.stay()?.id;
    if (!stayId || !this.minibarForm.itemName.trim() || this.minibarForm.unitPrice <= 0) return;

    this.acting.set(true);
    this.service.postMiniBarConsumption(stayId, {
      itemCode: this.minibarForm.itemCode || 'MINI',
      itemName: this.minibarForm.itemName.trim(),
      quantity: this.minibarForm.quantity,
      unitPrice: this.minibarForm.unitPrice
    }).subscribe({
      next: () => {
        this.minibarForm = { itemCode: '', itemName: '', quantity: 1, unitPrice: 0 };
        const reservationId = this.reservation()?.id;
        if (reservationId) this.loadStayForReservation(reservationId);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  checkOut(): void {
    const stayId = this.stay()?.id;
    if (!stayId) return;

    this.acting.set(true);
    this.service.checkOutHospitalityStay(stayId).subscribe({
      next: () => {
        this.reservation.update(r => r ? { ...r, status: 'CheckedOut' } : r);
        this.stay.update(s => s ? { ...s, status: 'CheckedOut' } : s);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  receivePrepayment(): void {
    const id = this.reservation()?.id;
    if (!id || this.prepaymentForm.amount <= 0) return;

    this.acting.set(true);
    this.service.receiveHospitalityPrepayment(id, {
      amount: this.prepaymentForm.amount,
      idempotencyKey: crypto.randomUUID()
    }).subscribe({
      next: () => {
        this.prepaymentForm.amount = 0;
        this.loadReservation(id);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  postRefund(): void {
    const id = this.reservation()?.id;
    if (!id || this.refundForm.amount <= 0) return;

    this.acting.set(true);
    this.service.postHospitalityRefund(id, {
      amount: this.refundForm.amount,
      reason: this.refundForm.reason || this.t('catalog.hospitality.defaultRefundReason'),
      idempotencyKey: crypto.randomUUID()
    }).subscribe({
      next: () => {
        this.refundForm = { amount: 0, reason: '' };
        this.loadReservation(id);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  postChannelCommission(): void {
    const id = this.reservation()?.id;
    if (!id || this.commissionForm.amount <= 0 || !this.commissionForm.channelCode.trim()) return;

    this.acting.set(true);
    this.service.postHospitalityChannelCommission(id, {
      channelCode: this.commissionForm.channelCode.trim(),
      amount: this.commissionForm.amount,
      idempotencyKey: crypto.randomUUID()
    }).subscribe({
      next: () => {
        this.commissionForm = { channelCode: '', amount: 0 };
        this.loadReservation(id);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
